import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query'
import { getAuditLog, type AuditLogQueryKeyEntries } from './audit-log.api'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries the fetcher reads; the scoped key builder below sets them. */
type AuditLogRecordQueryKey = [AdminQueryRoot & AuditLogQueryKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const auditLogQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'audit-log' }, { organizationId }),
  ],
  record: ({
    organizationId,
    subject,
    recordId,
    pageSize,
  }: AuditLogQueryKeyEntries): AuditLogRecordQueryKey =>
    buildScopedAdminQueryKey(auditLogQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'record',
      subject,
      recordId,
      pageSize,
    }),
}

export const auditLogQueries = {
  record: ({ organizationId, subject, recordId, pageSize = 20 }: AuditLogQueryKeyEntries) =>
    infiniteQueryOptions({
      queryKey: auditLogQueryKeys.record({
        organizationId,
        subject,
        recordId,
        pageSize,
      }),
      queryFn: getAuditLog,
      initialPageParam: null,
      getNextPageParam: (lastPage) =>
        lastPage?.meta?.has_more ? lastPage.meta.next_cursor || undefined : undefined,
    }),
}

export function useAuditLog({
  organizationId,
  subject,
  recordId,
  pageSize = 20,
  ...params
}: OrganizationScope &
  Omit<AuditLogQueryKeyEntries, 'organizationId'> &
  QueryParams<ReturnType<typeof auditLogQueries.record>>) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useInfiniteQuery({
    ...auditLogQueries.record({
      organizationId: resolvedOrganizationId,
      subject,
      recordId,
      pageSize,
    }),
    ...params,
  })
}
